// src/utils/proxyWs.js
// Single source of truth for the proxy WebSocket URL.
//
// Several pages (MainApp, AdminPanel, IuRewritePage) and the App status bar all need
// the same value. Previously each resolved it independently — some honored a
// stale `ws://localhost:9000` override left in localStorage from local testing,
// and the status bar hardcoded `localhost:9000` as its display fallback. On the
// production https:// dashboard a plain ws:// URL is blocked as mixed content,
// so the dashboard got stuck "Connecting…". Centralizing + self-healing here
// prevents any page from reintroducing the bad value.

import { getCachedProxyCredential } from '../api/proxyAuth';

const PROD_PROXY_WS = 'wss://proxy.adlensfx.com';
const LOCAL_PROXY_WS = 'ws://localhost:9000';
export const LOCAL_PROXY_API = 'http://localhost:8001';

export function isLocalHost(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

// Pure so it can be tested per hostname. A dashboard served from localhost with no
// REACT_APP_PROXY_WS_URL must NOT silently talk to the production proxy: a developer's
// "New session" / Clear would wipe PROD history. Local dev defaults to the local proxy.
export function defaultProxyWsUrl(envUrl, hostname) {
  if (envUrl) return envUrl;
  return isLocalHost(hostname) ? LOCAL_PROXY_WS : PROD_PROXY_WS;
}

// Same rule for the proxy REST API base (empty = caller's own relative fallback).
export function defaultProxyApiUrl(envUrl, hostname) {
  if (envUrl) return envUrl.replace(/\/$/, '');
  return isLocalHost(hostname) ? LOCAL_PROXY_API : '';
}

// Under Jest, jsdom's hostname is "localhost"; keep the production default there so
// suites that assert the prod hostnames (faviconSafety) stay meaningful. The local rule
// itself is covered through defaultProxyWsUrl() directly.
export const PROXY_WS_DEFAULT = defaultProxyWsUrl(
  process.env.REACT_APP_PROXY_WS_URL,
  typeof window !== 'undefined' && process.env.NODE_ENV !== 'test' ? window.location.hostname : ''
);

const LS_KEY = 'proxy_ws_url';

// Resolve the proxy WS URL, healing stale/insecure overrides.
export function resolveProxyWsUrl() {
  let url;
  try { url = localStorage.getItem(LS_KEY) || PROXY_WS_DEFAULT; }
  catch { url = PROXY_WS_DEFAULT; }

  // On an https page, a plain ws:// URL is blocked (mixed content) and is almost
  // always a stale local-testing override — drop it and use the secure default.
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  if (isSecure && /^ws:\/\//i.test(url)) {
    try { localStorage.removeItem(LS_KEY); } catch {}
    url = PROXY_WS_DEFAULT;
  }
  return url;
}

// Append the dashboard WebSocket credential as `?token=`, which is the only
// scheme a browser WebSocket can use — the WS API cannot set request headers,
// so Bearer auth is not available here.
//
// Precedence (docs/proxy-credential-spec.md): the short-lived per-user
// credential (api/proxyAuth.js's in-memory cache — sync read, same as this
// function must stay) beats the static REACT_APP_PROXY_WS_TOKEN, which stays
// only as the transition fallback for an environment without PROXY_JWT_SECRET.
//
// Applied ONLY at the `new WebSocket(...)` call sites, never to the value held
// in state or written to localStorage: the status bar renders that value, and a
// token in the UI (or in a stale localStorage override pasted into a bug report)
// is a leak. Callers that display the URL keep the clean one.
export function withWsToken(url) {
  const token = getCachedProxyCredential() || process.env.REACT_APP_PROXY_WS_TOKEN || '';
  if (!token || !url) return url;
  if (/[?&]token=/.test(url)) return url;          // caller already supplied one
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
}

// WS close code the proxy uses for a rejected/expired credential (wsGate.js).
export const WS_AUTH_CLOSE_CODE = 4401;

// Called from the reconnect loop's onclose. On 4401 it mints a fresh per-user
// credential (force) BEFORE the caller reconnects, so withWsToken() picks up the new
// token without a page reload. Returns true when a refresh was attempted. Never
// throws; a failed mint resolves false and the caller retries on its normal backoff.
// Once the static REACT_APP_PROXY_WS_TOKEN is removed there is no fallback to it.
export async function refreshCredentialOnAuthClose(code, refresh) {
  if (code !== WS_AUTH_CLOSE_CODE) return false;
  try {
    const fresh = await refresh({ force: true });
    return !!(fresh && fresh.token);
  } catch {
    return false;
  }
}

// Display form for the status bar (scheme stripped), never falls back to localhost.
export function proxyWsLabel(urlFromContext) {
  const u = urlFromContext || resolveProxyWsUrl();
  return u.replace(/^wss?:\/\//, '');
}
